import React, { useState } from 'react';
import { cn } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import ThemedSelect from '../shared/ThemedSelect';
import { useDirtyState } from '../../hooks/useDirtyState';
import { DirtyStateDialog } from '../shared/DirtyStateDialog';

const EditComment = ({ editComment, setEditComment, onUpdate, predictors, stocks, isOpen, setIsOpen }) => {
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isDirty, markDirty, resetDirty, showDialog, confirmNavigation, cancelNavigation } = useDirtyState(editComment);
    const [showCloseDialog, setShowCloseDialog] = useState(false);

    if (!editComment || !isOpen) return null;

    const predictorOptions = predictors.map((p) => ({
        value: p.predictor_id,
        label: `${p.predictor_id} - ${p.predictorname}`,
    }));

    const stockOptions = stocks.map((s) => ({
        value: s.stock_symbol,
        label: `${s.stock_symbol} - ${s.company_name}`,
    }));

    const handleInputChange = (field, value) => {
        setEditComment(prev => {
            const updated = { ...prev, [field]: value };
            markDirty(updated);
            return updated;
        });
    };

    const handleClose = () => {
        setIsOpen(false);
        setEditComment(null);
        setErrors({});
        resetDirty();
    };

    const handleDialogOpenChange = (open) => {
        if (!open && isDirty) {
            setShowCloseDialog(true);
        } else {
            setIsOpen(open);
            if (!open) {
                setEditComment(null);
                setErrors({});
                resetDirty();
            }
        }
    };

    const confirmClose = () => {
        setShowCloseDialog(false);
        setIsOpen(false);
        setEditComment(null);
        setErrors({});
        resetDirty();
    };

    const cancelClose = () => {
        setShowCloseDialog(false);
    };

    const validate = (data) => {
        const errs = {};
        if (!data.comment_text || data.comment_text.trim() === '') errs.comment_text = 'Ce champ est requis';
        else if (data.comment_text.length > 2000) errs.comment_text = 'Maximum 2000 caractères';
        if (!data.direction || data.direction.trim() === '') errs.direction = 'Ce champ est requis';
        if (!data.time_frame || data.time_frame.trim() === '') errs.time_frame = 'Ce champ est requis';
        if (data.percentage_change !== '' && data.percentage_change !== null && data.percentage_change !== undefined) {
            const num = Number(data.percentage_change);
            if (isNaN(num)) errs.percentage_change = 'Valeur numérique requise';
        }
        if (!data.fk_predictor_id || String(data.fk_predictor_id).trim() === '') errs.fk_predictor_id = 'Ce champ est requis';
        if (!data.fk_stock_symbol || String(data.fk_stock_symbol).trim() === '') errs.fk_stock_symbol = 'Ce champ est requis';
        return errs;
    };

    const handleSubmit = async () => {
        const validationErrors = validate(editComment);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setIsSubmitting(true);
        try {
            await onUpdate();
            resetDirty();
            setIsOpen(false);
            setEditComment(null);
        } catch (e) {
            // Error handled in parent
        } finally {
            setIsSubmitting(false);
        }
    };

    const isSubmitDisabled = isSubmitting || Object.keys(validate(editComment)).length > 0;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
                <DialogContent className="max-w-2xl pb-20">
                    <DialogHeader>
                        <DialogTitle>Modifier le commentaire</DialogTitle>
                        <DialogDescription>
                            Modifiez les champs du commentaire.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                        {/* Commentaire - full width */}
                        <div className="md:col-span-2">
                            <Label htmlFor="edit-comment-text">Commentaire *</Label>
                            <textarea
                                id="edit-comment-text"
                                value={editComment.comment_text}
                                onChange={(e) => handleInputChange('comment_text', e.target.value)}
                                className={cn(
                                    "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none",
                                    errors.comment_text && "border-destructive"
                                )}
                                placeholder="Entrez le commentaire..."
                            />
                            {errors.comment_text && <p className="text-sm text-destructive mt-1">{errors.comment_text}</p>}
                        </div>

                        {/* Direction */}
                        <div>
                            <Label htmlFor="edit-direction">Direction *</Label>
                            <Input
                                id="edit-direction"
                                value={editComment.direction}
                                onChange={(e) => handleInputChange('direction', e.target.value)}
                                className={errors.direction ? "border-destructive" : ""}
                            />
                            {errors.direction && <p className="text-sm text-destructive mt-1">{errors.direction}</p>}
                        </div>

                        {/* Time frame */}
                        <div>
                            <Label htmlFor="edit-time-frame">Time frame *</Label>
                            <Input
                                id="edit-time-frame"
                                value={editComment.time_frame}
                                onChange={(e) => handleInputChange('time_frame', e.target.value)}
                                className={errors.time_frame ? "border-destructive" : ""}
                            />
                            {errors.time_frame && <p className="text-sm text-destructive mt-1">{errors.time_frame}</p>}
                        </div>

                        {/* Percentage change */}
                        <div>
                            <Label htmlFor="edit-pct">Changement (%)</Label>
                            <Input
                                id="edit-pct"
                                type="number"
                                step="0.01"
                                value={editComment.percentage_change}
                                onChange={(e) => handleInputChange('percentage_change', e.target.value)}
                                className={errors.percentage_change ? "border-destructive" : ""}
                            />
                            {errors.percentage_change && <p className="text-sm text-destructive mt-1">{errors.percentage_change}</p>}
                        </div>

                        {/* Empty placeholder to balance grid */}
                        <div className="hidden md:block" />

                        {/* Prédicteur */}
                        <div>
                            <Label htmlFor="edit-predictor">Prédicteur *</Label>
                            <ThemedSelect
                                options={predictorOptions}
                                value={predictorOptions.find(opt => String(opt.value) === String(editComment.fk_predictor_id)) || null}
                                placeholder="Sélectionner un prédicteur"
                                onChange={(opt) => handleInputChange('fk_predictor_id', opt ? opt.value : '')}
                                isClearable
                            />
                            {errors.fk_predictor_id && <p className="text-sm text-destructive mt-1">{errors.fk_predictor_id}</p>}
                        </div>

                        {/* Stock */}
                        <div>
                            <Label htmlFor="edit-stock">Stock *</Label>
                            <ThemedSelect
                                options={stockOptions}
                                value={stockOptions.find(opt => opt.value === editComment.fk_stock_symbol) || null}
                                placeholder="Sélectionner un stock"
                                onChange={(opt) => handleInputChange('fk_stock_symbol', opt ? opt.value : '')}
                                isClearable
                            />
                            {errors.fk_stock_symbol && <p className="text-sm text-destructive mt-1">{errors.fk_stock_symbol}</p>}
                        </div>
                    </div>

                    <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                        <Button variant="outline" onClick={handleClose}>
                            Annuler
                        </Button>
                        <Button variant="default" disabled={isSubmitDisabled} onClick={handleSubmit}>
                            {isSubmitting ? (
                                <>
                                    <Spinner className="h-4 w-4 mr-2" />
                                    Enregistrement...
                                </>
                            ) : (
                                'Enregistrer'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DirtyStateDialog open={showDialog} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
            <DirtyStateDialog open={showCloseDialog} onConfirm={confirmClose} onCancel={cancelClose} />
        </>
    );
};

export default EditComment;
